/**
 * AdminCreatePersonaPage - Create New AI Persona
 *
 * Feature: 071-ai-critique-sessions (US2, T046)
 * Form for creating new AI personas.
 */

import { Link, useNavigate } from 'react-router-dom';
import { useCreateAIPersona, CreateAIPersonaInput, UpdateAIPersonaInput } from '@/hooks/useAIPersonas';
import { PersonaForm } from '@/components/composed/PersonaForm';
import logger from '@/utils/logger';
import { toast } from 'sonner';
import { adminAIPersonasUrl, adminAIPersonaDetailUrl } from '@/utils/urls';

export function AdminCreatePersonaPage() {
  const navigate = useNavigate();
  const { createPersona, loading } = useCreateAIPersona();

  const handleCreate = async (data: CreateAIPersonaInput | UpdateAIPersonaInput) => {
    try {
      // For create mode, data will always have all required fields
      const createData = data as CreateAIPersonaInput;
      const newPersona = await createPersona(createData);
      toast.success(`Created ${newPersona.displayName}`);
      navigate(adminAIPersonaDetailUrl(newPersona.globalId));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create persona';
      logger.error('Admin', 'Failed to create persona', err);
      toast.error(errorMessage);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Create AI Persona</h1>
            <p className="text-muted mt-1">Set up a new AI critic persona</p>
          </div>
          <Link
            to={adminAIPersonasUrl()}
            className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
          >
            Back to List
          </Link>
        </div>

        {/* Form */}
        <div className="bg-surface-1 rounded-lg border border-border p-6">
          <PersonaForm
            onSubmit={handleCreate}
            onCancel={() => navigate(adminAIPersonasUrl())}
            isSubmitting={loading}
            submitLabel="Create Persona"
          />
        </div>
      </div>
    </div>
  );
}
